(() => {
  const builder = document.querySelector('[data-set-builder]');
  if (!builder) return;

  const panels = [...builder.querySelectorAll('[data-builder-panel]')];
  const panelLinks = [...builder.querySelectorAll('[data-builder-panel-link]')];
  const cards = [...builder.querySelectorAll('[data-builder-card]')];
  const summary = builder.querySelector('[data-builder-summary]');
  const empty = builder.querySelector('[data-builder-empty]');
  const total = builder.querySelector('[data-builder-total]');
  const submit = builder.querySelector('[data-builder-submit]');
  const status = builder.querySelector('[data-builder-status]');
  const currency = builder.dataset.currency || 'USD';

  const money = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency',
    currency,
  });
  const escapeHtml = (value = '') => String(value).replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  }[character]));

  const selectedCards = () => cards.filter((card) => card.classList.contains('is-selected'));
  const readCard = (card) => {
    const option = card.querySelector('[data-builder-variant]').selectedOptions[0];
    const quantity = Math.max(1, Number(card.querySelector('[data-builder-quantity]').value || 1));
    return {
      card,
      variantId: option.value,
      variantTitle: option.dataset.title,
      available: option.dataset.available === 'true',
      price: Number(option.dataset.price),
      quantity,
      component: card.dataset.component,
      productTitle: card.dataset.productTitle,
      productImage: option.dataset.image || card.dataset.productImage,
    };
  };

  const syncVariant = (card) => {
    const variant = card.querySelector('[data-builder-variant]');
    const option = variant?.selectedOptions[0];
    if (!option) return;

    const available = option.dataset.available === 'true';
    const toggle = card.querySelector('[data-builder-toggle]');
    const price = card.querySelector('[data-builder-price]');
    const image = card.querySelector('.builder-card__preview-image');
    const imageLink = card.querySelector('.builder-card__image');

    if (price) price.textContent = money.format(Number(option.dataset.price || 0) / 100);
    if (image && option.dataset.image && image.getAttribute('src') !== option.dataset.image) {
      image.classList.add('is-switching');
      image.removeAttribute('srcset');
      image.src = option.dataset.image;
      image.alt = option.dataset.imageAlt || card.dataset.productTitle;
      const finishImageSwap = () => image.classList.remove('is-switching');
      if (image.complete) requestAnimationFrame(finishImageSwap);
      else image.addEventListener('load', finishImageSwap, { once: true });
    }
    if (imageLink) imageLink.href = `${card.dataset.productUrl}?variant=${option.value}`;

    if (!available && card.classList.contains('is-selected')) card.classList.remove('is-selected');
    if (toggle) {
      toggle.disabled = !available;
      const label = toggle.querySelector('span');
      const marker = toggle.querySelector('b');
      if (label) label.textContent = available ? (card.classList.contains('is-selected') ? 'Selected' : 'Select') : 'Sold out';
      if (marker) marker.textContent = card.classList.contains('is-selected') ? '✓' : '+';
    }
    updateSummary();
  };

  const syncPanelNavigation = () => {
    panelLinks.forEach((link) => {
      const panel = panels.find((candidate) => candidate.dataset.builderPanel === link.dataset.builderPanelLink);
      const expanded = Boolean(panel?.open);
      link.classList.toggle('is-active', expanded);
      link.setAttribute('aria-expanded', String(expanded));
    });
  };

  panels.forEach((panel) => {
    panel.addEventListener('toggle', () => {
      if (panel.open) {
        panels.forEach((otherPanel) => {
          if (otherPanel !== panel && otherPanel.open) otherPanel.open = false;
        });
      }
      syncPanelNavigation();
    });
  });

  panelLinks.forEach((link) => {
    link.addEventListener('click', () => {
      const panel = panels.find((candidate) => candidate.dataset.builderPanel === link.dataset.builderPanelLink);
      if (!panel) return;
      panel.open = true;
      syncPanelNavigation();
      requestAnimationFrame(() => panel.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start',
      }));
    });
  });

  function updateSummary() {
    const items = selectedCards().map(readCard);
    panels.forEach((panel) => {
      const selectedCount = panel.querySelectorAll('[data-builder-card].is-selected').length;
      const count = panel.querySelector('[data-builder-panel-count]');
      if (count) count.textContent = selectedCount ? `${selectedCount} selected` : 'No selection';
      panel.classList.toggle('has-selection', selectedCount > 0);
      const panelLink = panelLinks.find((link) => link.dataset.builderPanelLink === panel.dataset.builderPanel);
      panelLink?.classList.toggle('has-selection', selectedCount > 0);
    });
    empty.hidden = items.length > 0;
    submit.disabled = items.length === 0 || items.some((item) => !item.available);
    total.textContent = money.format(items.reduce((sum, item) => sum + item.price * item.quantity, 0) / 100);
    summary.innerHTML = items.map((item) => `
      <div class="builder-summary-item">
        ${item.productImage ? `<img src="${escapeHtml(item.productImage)}" alt="">` : '<span class="builder-summary-item__placeholder">J</span>'}
        <div><small>${escapeHtml(item.component)}</small><strong>${escapeHtml(item.productTitle)}</strong>${item.variantTitle !== 'Default Title' ? `<span>${escapeHtml(item.variantTitle)}</span>` : ''}<span>${item.quantity} × ${money.format(item.price / 100)}</span></div>
        <button type="button" data-builder-remove="${escapeHtml(item.variantId)}" aria-label="Remove ${escapeHtml(item.productTitle)}">×</button>
      </div>
    `).join('');
  }

  cards.forEach((card) => {
    const toggle = card.querySelector('[data-builder-toggle]');
    const quantity = card.querySelector('[data-builder-quantity]');
    const variant = card.querySelector('[data-builder-variant]');

    toggle?.addEventListener('click', () => {
      const selected = card.classList.toggle('is-selected');
      toggle.querySelector('span').textContent = selected ? 'Selected' : 'Select';
      const marker = toggle.querySelector('b');
      if (marker) marker.textContent = selected ? '✓' : '+';
      updateSummary();
    });
    card.querySelector('[data-builder-minus]')?.addEventListener('click', () => {
      quantity.value = Math.max(1, Number(quantity.value || 1) - 1);
      updateSummary();
    });
    card.querySelector('[data-builder-plus]')?.addEventListener('click', () => {
      quantity.value = Math.max(1, Number(quantity.value || 1) + 1);
      updateSummary();
    });
    quantity?.addEventListener('change', () => {
      quantity.value = Math.max(1, Number(quantity.value || 1));
      updateSummary();
    });
    variant?.addEventListener('change', () => syncVariant(card));
    syncVariant(card);
  });

  summary.addEventListener('click', (event) => {
    const button = event.target.closest('[data-builder-remove]');
    if (!button) return;
    const card = selectedCards().find((candidate) => candidate.querySelector('[data-builder-variant]').value === button.dataset.builderRemove);
    if (!card) return;
    card.classList.remove('is-selected');
    card.querySelector('[data-builder-toggle] span').textContent = 'Select';
    const marker = card.querySelector('[data-builder-toggle] b');
    if (marker) marker.textContent = '+';
    updateSummary();
  });

  submit.addEventListener('click', async () => {
    const selections = selectedCards().map(readCard);
    if (!selections.length) return;
    const setId = `mahj-${Date.now().toString(36)}`;
    submit.disabled = true;
    submit.classList.add('is-loading');
    status.textContent = 'Adding your set…';

    try {
      const response = await fetch(builder.dataset.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: selections.map((item) => ({
            id: Number(item.variantId),
            quantity: item.quantity,
            properties: {
              _custom_set_id: setId,
              Bundle: 'Custom Mahjong Set',
              Component: item.component,
            },
          })),
          sections: ['cart-drawer'],
          sections_url: `${window.location.pathname}${window.location.search}`,
        }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || 'Your set could not be added. Please check availability and try again.');
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cartSectionHtml: payload.sections?.['cart-drawer'], open: true, source: 'set-builder' },
      }));
      status.textContent = 'Your set was added to the cart.';
      submit.disabled = false;
      submit.classList.remove('is-loading');
    } catch (error) {
      status.textContent = error.message;
      submit.disabled = false;
      submit.classList.remove('is-loading');
    }
  });

  syncPanelNavigation();
  updateSummary();
})();
